import * as React from 'react';
import { FC, useCallback, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { ProfessionalEntity } from '../../store/entities/state';
import { WaitingListItem } from '../../models';
import { addToWaitingListAsync, getWaitingListByProfessionalAsync, removeFromWaitingListAsync } from '../../ajax/requests';
import { useGeneralSettings } from '../../store/settings/hooks';
import { serviceName } from '../../shared/appointmint/appointment';
import WaitingListItemForm from '../../components/Forms/WaitingListItemForm';
import { paths } from '../../app/constants';
import StyledTable from '../../components/StyledTable';
import AppButton from '../../components/AppButton';

export interface ProfessionalWaitingListProps {
    isOpen?: boolean;
    professional: ProfessionalEntity;
}

const SecondaryCellText = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 1.9rem;
    font-size: 0.85rem;
    font-weight: 400;
    color: var(--text-muted);
    line-height: 1.35;
`;

const DateText = styled(SecondaryCellText)<{ $stale: boolean }>`
    color: ${({ $stale }) => $stale ? 'var(--warning-color-strong)' : 'var(--text-muted)'};
`;

const EntityLinkButton = styled.button`
    border: 0;
    padding: 0;
    background: none;
    color: var(--text-muted);
    font-size: 0.85rem;
    font-weight: 400;
    line-height: 1.35;
    text-align: left;

    &:hover,
    &:focus {
        color: var(--brand-secondary);
        text-decoration: none;
    }
`;

const RowActionGroup = styled.td`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-XXS);
`;

const ProfessionalWaitingList: FC<ProfessionalWaitingListProps> = ({ isOpen = true, professional }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();

    const [items, setItems] = useState<WaitingListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [formValue, setFormValue] = useState<Partial<WaitingListItem>>({
        professionalId: professional.id,
        professionalName: professional.name,
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getWaitingListByProfessionalAsync(professional.id)
            .then(setItems)
            .finally(() => setLoading(false));
    }, [isOpen, professional.id]);

    const openModal = useCallback(() => {
        setFormValue({ professionalId: professional.id, professionalName: professional.name });
        setModalOpen(true);
    }, [professional.id, professional.name]);

    const handleAdd = useCallback(async () => {
        if (!formValue.clientId || !formValue.service) return;
        setSaving(true);
        try {
            await addToWaitingListAsync(formValue as WaitingListItem);
            const updated = await getWaitingListByProfessionalAsync(professional.id);
            setItems(updated);
            setModalOpen(false);
        } finally {
            setSaving(false);
        }
    }, [formValue, professional.id]);

    const handleRemove = useCallback((item: WaitingListItem) => async () => {
        await removeFromWaitingListAsync(item.clientId, item.service, item.professionalId);
        setItems(prev => prev.filter(i => !(i.clientId === item.clientId && i.service === item.service && i.professionalId === item.professionalId)));
    }, []);

    return (
        <>
            <div className="topButtonsContainer">
                <AppButton color="success" size="sm" onClick={openModal}>{t(TK.addToWaitingList)}</AppButton>
            </div>

            <StyledTable scrollable hover>
                <thead>
                    <tr>
                        <th>{t(TK.client)}</th>
                        <th>{t(TK.service)}</th>
                        <th>{t(TK.addedBy)}</th>
                        <th>{t(TK.date)}</th>
                        <th>{t(TK.notes)}</th>
                        <th aria-label={t(TK.action)} />
                    </tr>
                </thead>
                <tbody>
                    {!items.length && !loading && (
                        <tr>
                            <td colSpan={6}><i>{t(TK.noEntries)}</i></td>
                        </tr>
                    )}
                    {loading && (
                        <tr>
                            <td colSpan={6}><i>{t(TK.loading)}</i></td>
                        </tr>
                    )}
                    {items.map((item, idx) => {
                        const isStale = moment().diff(moment(item.addedAt), 'days') > 7;
                        const dateLabel = item.addedAt ? moment(item.addedAt).format('MMM D, YYYY') : '-';

                        return (
                            <tr key={idx}>
                                <td>
                                    <EntityLinkButton
                                        type="button"
                                        onClick={() => dispatch(push({ pathname: paths.clients(), search: `?id=${item.clientId}` }))}
                                    >
                                        {item.clientName || '-'}
                                    </EntityLinkButton>
                                </td>
                                <td>
                                    <SecondaryCellText>
                                        {serviceName(item.service, generalSettings)}
                                    </SecondaryCellText>
                                </td>
                                <td>
                                    <SecondaryCellText>{item.addedByName || '-'}</SecondaryCellText>
                                </td>
                                <td>
                                    <DateText $stale={isStale}>{dateLabel}</DateText>
                                </td>
                                <td>
                                    <SecondaryCellText>{item.notes || '-'}</SecondaryCellText>
                                </td>
                                <RowActionGroup>
                                    <Tooltip title={t(TK.remove)}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleRemove(item)}
                                                style={{ color: 'var(--danger-color)' }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </RowActionGroup>
                            </tr>
                        );
                    })}
                </tbody>
            </StyledTable>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader toggle={() => setModalOpen(false)}>{t(TK.addToWaitingList)}</ModalHeader>
                <ModalBody>
                    <WaitingListItemForm value={formValue} onChange={setFormValue} />
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        color="success"
                        disabled={!formValue.clientId || !formValue.service || saving}
                        onClick={handleAdd}
                    >
                        {t(TK.add)}
                    </AppButton>
                    <AppButton color="secondary" onClick={() => setModalOpen(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ProfessionalWaitingList;
